import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { toast } from "sonner";
import { Sunrise, Send, Eye, CalendarRange, BellRing } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRIGGER = { cron: "Automatico", preview: "Anteprima" };
const KIND = { daily: "Giornaliero", weekly: "Settimanale" };
const MODE = { smtp: "inviato", mock: "solo app", error: "errore invio" };

const Toggle = ({ on, onClick, testid }) => (
  <button className={`h-7 w-12 rounded-full flex items-center px-1 transition-colors shrink-0 ${on ? "bg-emerald-500" : "bg-gray-300"}`} onClick={onClick} data-testid={testid} aria-pressed={on}>
    <div className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : ""}`} />
  </button>
);

export const DigestSettings = () => {
  const [settings, setSettings] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [sending, setSending] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([axios.get(`${API}/csm/digest/settings`), axios.get(`${API}/csm/digest/outbox`)]);
      setSettings(s.data);
      setOutbox(o.data.items || []);
    } catch { toast.error("Errore caricamento riepilogo"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (field) => {
    try {
      const r = await axios.put(`${API}/csm/digest/settings`, { [field]: !settings[field] });
      setSettings(r.data);
      const name = { weekly_enabled: "Riepilogo settimanale", alerts_enabled: "Allerta clienti in fascia rossa", enabled: "Riepilogo mattutino" }[field];
      toast.success(`${name}: ${r.data[field] ? "attivo" : "disattivato"}`);
    } catch { toast.error("Errore"); }
  };

  const preview = async (kind) => {
    setSending(kind);
    try {
      const r = await axios.post(`${API}/csm/digest/${kind === "weekly" ? "weekly-preview" : "preview"}`);
      if (r.data.mode === "smtp") toast.success(`Inviato a ${r.data.to}`);
      if (r.data.mode === "error") toast.error("Invio email non riuscito: ricollega Gmail");
      setViewing(r.data);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSending(false);
  };

  const open = async (id) => {
    try { setViewing((await axios.get(`${API}/csm/digest/${id}`)).data); } catch { toast.error("Errore"); }
  };

  if (!settings) return <div className="py-6 flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="digest-settings">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Sunrise className="h-5 w-5 text-amber-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Ricevi riepilogo mattutino</p>
            <p className="text-xs text-muted-foreground">{settings.schedule} · i tuoi task, contratti in scadenza, clienti a rischio, email urgenti</p>
          </div>
        </div>
        <Toggle on={settings.enabled} onClick={() => toggle("enabled")} testid="digest-toggle" />
      </div>
      <Button variant="outline" className="gap-2 h-9 text-sm" onClick={() => preview("daily")} disabled={!!sending} data-testid="digest-preview-btn">
        <Send className="h-4 w-4" /> {sending === "daily" ? "Generazione..." : "Invia anteprima ora"}
      </Button>
      <div className="flex items-center justify-between gap-4 pt-3 border-t border-border/50" data-testid="alerts-settings">
        <div className="flex items-start gap-3">
          <BellRing className="h-5 w-5 text-red-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Allerta clienti in fascia rossa</p>
            <p className="text-xs text-muted-foreground">Email immediata quando un tuo cliente (o non assegnato) passa da verde/giallo a rosso. La notifica in app arriva sempre.</p>
          </div>
        </div>
        <Toggle on={settings.alerts_enabled} onClick={() => toggle("alerts_enabled")} testid="alerts-toggle" />
      </div>
      {settings.weekly_enabled !== null && settings.weekly_enabled !== undefined && (
        <div className="pt-3 border-t border-border/50 space-y-3" data-testid="weekly-digest-settings">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <CalendarRange className="h-5 w-5 text-violet-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Riepilogo settimanale Admin</p>
                <p className="text-xs text-muted-foreground">{settings.weekly_schedule} · nuovo ARR, churn, Net ARR, ARR totale vs settimana prima, top sales, clienti a rischio, task scaduti del team</p>
              </div>
            </div>
            <Toggle on={settings.weekly_enabled} onClick={() => toggle("weekly_enabled")} testid="weekly-digest-toggle" />
          </div>
          <Button variant="outline" className="gap-2 h-9 text-sm" onClick={() => preview("weekly")} disabled={!!sending} data-testid="weekly-digest-preview-btn">
            <Send className="h-4 w-4" /> {sending === "weekly" ? "Generazione..." : "Invia anteprima settimanale"}
          </Button>
        </div>
      )}
      {settings.mode === "smtp" ? (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5" data-testid="digest-sender-info">
          I riepiloghi vengono inviati dalla tua Gmail ({settings.sender}) a te stesso.
        </p>
      ) : (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5" data-testid="digest-mock-warning">
          Gmail non collegata: i riepiloghi vengono salvati solo qui. Collega la tua Gmail in CSM → Email per riceverli via email.
        </p>
      )}
      {outbox.length > 0 && (
        <div className="space-y-1" data-testid="digest-outbox">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Ultimi riepiloghi</p>
          {outbox.slice(0, 5).map(d => (
            <button key={d.digest_id} onClick={() => open(d.digest_id)} className="w-full flex items-center justify-between text-xs px-3 py-2 rounded bg-muted/30 hover:bg-muted/60 transition-colors" data-testid={`digest-item-${d.digest_id}`}>
              <span>{KIND[d.kind || "daily"]} · {new Date(d.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Rome" })} · {TRIGGER[d.trigger]} · {MODE[d.mode] || "solo app"}</span>
              <span className="text-muted-foreground flex items-center gap-2">
                {d.kind === "weekly" ? `${d.counts.new_contracts} nuovi · ${d.counts.at_risk} a rischio · ${d.counts.overdue_tasks} task scaduti` : `${d.counts.tasks} task · ${d.counts.contracts} contratti · ${d.counts.at_risk} a rischio · ${d.counts.emails} email`} <Eye className="h-3 w-3" />
              </span>
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="digest-preview-dialog">
          <DialogHeader><DialogTitle className="text-base">{viewing?.subject} <span className="text-xs font-normal text-muted-foreground">→ {viewing?.to}</span></DialogTitle></DialogHeader>
          <div className="w-full h-[60vh] overflow-y-auto rounded border border-border bg-white p-4" dangerouslySetInnerHTML={{ __html: viewing?.html || "" }} data-testid="digest-preview-frame" />
        </DialogContent>
      </Dialog>
    </div>
  );
};
